import { Arrange, Icon, SvgChevronDown, Text, Tooltip } from '@loomhq/lens';
import React, { useEffect, useMemo, useState } from 'react';
import { useLayer } from 'react-laag';
import ResizeObserver from 'resize-observer-polyfill';

import { VideoVisibility as VisibilityType } from 'constants/visibility';

import { useGetSelectedWorkspace } from 'hooks/workspace';

import VisibilityPopover from './VisibilityPopover';
import { VideoCardVideo } from './types';

import styles from './styles.module.less';

type VideoVisibilityProps = {
  video: VideoCardVideo;
  textSize?: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'xxxlarge';
  // onVisibilityUpdate is a callback to execute once the video visibility has updated
  onVisibilityUpdate?: (visibility: string) => void;
  context: string;
  showPostVideoFTUX: boolean;
  setHasActiveHoverState?: (hasActiveHoverState: boolean) => void;
};

export const VideoVisibility = ({
  video,
  textSize = 'medium',
  onVisibilityUpdate,
  showPostVideoFTUX,
  context,
  setHasActiveHoverState,
}: VideoVisibilityProps): React.ReactElement => {
  const [isVisibilityPopoverOpen, setIsVisibilityPopoverOpen] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const workspace = useGetSelectedWorkspace();

  const { visibility: videoVisibility } = video;

  useEffect(() => {
    if (showPostVideoFTUX) {
      setIsVisibilityPopoverOpen(true);
    }
  }, [showPostVideoFTUX]);

  useEffect(() => {
    setHasActiveHoverState && setHasActiveHoverState(isVisibilityPopoverOpen);
  }, [setHasActiveHoverState, isVisibilityPopoverOpen]);

  const { renderLayer, triggerProps, layerProps } = useLayer({
    isOpen: isVisibilityPopoverOpen,
    ResizeObserver,
    placement: 'bottom-start',
    auto: true,
  });

  useMemo(() => {
    const isPublished = videoVisibility?.includes(
      VisibilityType.WORKSPACE,
      // @ts-expect-error ignoring in bulk: https://github.com/loomhq/loom/pull/10341
      VisibilityType.PUBLIC
    );

    setIsPublished(isPublished);
  }, [videoVisibility]);

  const onOpenPopOver = () => {
    setIsVisibilityPopoverOpen(!isVisibilityPopoverOpen);
  };

  const publishedText = (
    <Text fontWeight="medium" color="primary" size={textSize}>
      Posted
    </Text>
  );
  const unpublishedText = (
    <Text color="bodyDimmed" size={textSize}>
      Not posted
    </Text>
  );

  return (
    <>
      <Tooltip
        content={isPublished ? `Posted to ${workspace.name}` : undefined}
        placement="rightCenter"
      >
        <button
          className={styles.visibilityButton}
          {...triggerProps}
          onClick={onOpenPopOver}
        >
          <Arrange gap="xsmall">
            {isPublished ? publishedText : unpublishedText}
            <Icon icon={<SvgChevronDown />} size="1.5" />
          </Arrange>
        </button>
      </Tooltip>

      {isVisibilityPopoverOpen &&
        renderLayer(
          <span className={styles.visibilityCard} {...layerProps}>
            <VisibilityPopover
              video={video}
              isPublished={isPublished}
              setIsVisibilityPopoverOpen={setIsVisibilityPopoverOpen}
              onVisibilityUpdate={onVisibilityUpdate}
              context={context}
              setIsPublished={setIsPublished}
              showPostVideoFTUX={showPostVideoFTUX}
            />
          </span>
        )}
    </>
  );
};
